class ProductUpload {
  constructor(root) {
    this.root = root;
    this.sectionId = root.id.replace('ProductUpload-', '');
    this.input = root.querySelector('.upload-dropzone__input');
    this.dropzone = root.querySelector('.upload-dropzone');
    this.prompt = root.querySelector('.upload-dropzone__prompt');
    this.preview = root.querySelector('.upload-dropzone__preview');
    this.previewImg = root.querySelector('.upload-dropzone__preview-img');
    this.filename = root.querySelector('.upload-dropzone__filename');
    this.progressFill = root.querySelector('.upload-dropzone__progress-fill');
    this.removeBtn = root.querySelector('.upload-dropzone__remove');
    this.errorEl = root.parentElement.querySelector('.upload-dropzone__error');

    this.modal = document.getElementById(`CropModal-${this.sectionId}`);
    this.modalImg = this.modal.querySelector('.crop-modal__image');
    this.zoomSlider = this.modal.querySelector('.crop-modal__zoom');
    this.cropper = null;

    this.maxSizeBytes = 20 * 1024 * 1024; // Shopify's 20MB per-order cap on upload properties
    this.allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/tiff', 'image/webp'];

    this.input.addEventListener('change', () => this.handleFiles(this.input.files));

    this.dropzone.addEventListener('click', (e) => {
      if (e.target.closest('.upload-dropzone__remove')) return;
      this.input.click();
    });

    this.removeBtn.addEventListener('click', (e) => this.clear(e));

    ['dragenter', 'dragover'].forEach((evt) =>
      this.dropzone.addEventListener(evt, (e) => {
        e.preventDefault();
        this.dropzone.classList.add('is-dragover');
      })
    );
    ['dragleave', 'drop'].forEach((evt) =>
      this.dropzone.addEventListener(evt, (e) => {
        e.preventDefault();
        this.dropzone.classList.remove('is-dragover');
      })
    );
    this.dropzone.addEventListener('drop', (e) => {
      if (e.dataTransfer.files.length) this.handleFiles(e.dataTransfer.files);
    });

    this.modal.querySelector('.crop-modal__close').addEventListener('click', () => this.closeModal());
    this.modal.querySelectorAll('.crop-modal__rotate').forEach((btn) =>
      btn.addEventListener('click', () => this.cropper && this.cropper.rotate(Number(btn.dataset.direction)))
    );
    this.zoomSlider.addEventListener('input', () => {
      if (this.cropper) this.cropper.zoomTo(Number(this.zoomSlider.value));
    });
    this.modal.querySelector('.crop-modal__confirm').addEventListener('click', () => this.confirmCrop());

    this.setupAddToCartValidation();
  }

  // Block add-to-cart until an image has been uploaded and cropped.
  // Listeners are in the capture phase so they run before the theme's own
  // AJAX add-to-cart handler, and stopImmediatePropagation() prevents it firing.
  setupAddToCartValidation() {
    this.form =
      this.input.form ||
      document.querySelector(`form[action*="/cart/add"][id*="${this.sectionId}"]`) ||
      document.querySelector('form[action*="/cart/add"]');

    if (!this.form) return;

    this.form.addEventListener('submit', (e) => this.validateBeforeSubmit(e), true);

    // Fallback for themes whose add-to-cart button triggers fetch() on click
    // without going through the form's submit event.
    const submitBtn = this.form.querySelector('[type="submit"], [name="add"]');
    if (submitBtn) {
      submitBtn.addEventListener('click', (e) => this.validateBeforeSubmit(e), true);
    }
  }

  hasImage() {
    return !!(this.input.files && this.input.files.length > 0 && !this.preview.hidden);
  }

  validateBeforeSubmit(e) {
    if (this.hasImage()) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    this.showError('Please upload your image before adding to cart.');
    this.dropzone.classList.add('has-error');
    this.dropzone.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // The Size options live inside a separate <variant-picker> component (its own
  // <form class="variant-picker__form">), not inside the add-to-cart form the
  // upload input is bound to. So we match the variant-picker by its shared
  // data-section-id, then find the option group whose legend/label says "Size"
  // specifically (since Paper Type / Canvas Edge use the same radio markup).
  getSelectedSizeAspectRatio() {
    const variantPicker = document.querySelector(`variant-picker[data-section-id="${this.sectionId}"]`);
    if (!variantPicker) return null;

    const groups = variantPicker.querySelectorAll('.variant-option');
    let rawValue = '';

    groups.forEach((group) => {
      const labelText = group.querySelector('legend, label')?.textContent || '';
      if (/size/i.test(labelText)) {
        const checkedRadio = group.querySelector('input[type="radio"]:checked');
        const select = group.querySelector('select');
        rawValue = checkedRadio?.value || select?.selectedOptions?.[0]?.value || '';
      }
    });

    const match = rawValue.match(/(\d+(?:\.\d+)?)\s*(?:cm|mm)?\s*x\s*(\d+(?:\.\d+)?)/i);
    if (!match) return null;

    // First number = width, second = height — "60x90" is portrait (0.667),
    // "90x60" is landscape (1.5). Matches the size label's literal order.
    const width = parseFloat(match[1]);
    const height = parseFloat(match[2]);
    return width / height;
  }

  handleFiles(fileList) {
    this.hideError();
    const file = fileList[0];
    if (!file) return;

    if (!this.allowedTypes.includes(file.type)) {
      this.showError('Please upload a JPG, PNG, TIFF, or WEBP image.');
      return;
    }
    if (file.size > this.maxSizeBytes) {
      this.showError('File is too large. Max size is 20MB.');
      return;
    }

    this.pendingFilename = file.name.replace(/\.[^/.]+$/, '') + '.png';
    const reader = new FileReader();
    reader.onload = (e) => this.openModal(e.target.result);
    reader.readAsDataURL(file);
  }

  openModal(dataUrl) {
    this.modal.hidden = false;
    this.modalImg.src = dataUrl;

    if (this.cropper) this.cropper.destroy();
    this.zoomSlider.value = 0.2;

    const aspectRatio = this.getSelectedSizeAspectRatio();

    requestAnimationFrame(() => {
      this.cropper = new Cropper(this.modalImg, {
        viewMode: 1,
        dragMode: 'move',
        autoCropArea: 1,
        background: false,
        responsive: true,
        zoomOnWheel: true,
        aspectRatio: aspectRatio || NaN, // NaN = free-form crop if size can't be parsed
      });
    });
  }

  closeModal() {
    this.modal.hidden = true;
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }
    if (this.preview.hidden) this.input.value = '';
  }

  confirmCrop() {
    if (!this.cropper) return;
    const canvas = this.cropper.getCroppedCanvas({ imageSmoothingQuality: 'high' });

    canvas.toBlob((blob) => {
      const file = new File([blob], this.pendingFilename, { type: 'image/png' });
      const dt = new DataTransfer();
      dt.items.add(file);
      this.input.files = dt.files;

      this.previewImg.src = canvas.toDataURL('image/png');
      this.filename.textContent = this.pendingFilename;
      this.progressFill.style.width = '100%';
      this.prompt.hidden = true;
      this.preview.hidden = false;

      this.hideError(); // clear the "please upload" validation error
      this.closeModal();
    }, 'image/png');
  }

  clear(e) {
    e.stopPropagation();
    this.input.value = '';
    this.progressFill.style.width = '0%';
    this.preview.hidden = true;
    this.prompt.hidden = false;
    this.previewImg.src = '';
  }

  showError(msg) {
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  }
  hideError() {
    this.errorEl.hidden = true;
    this.dropzone.classList.remove('has-error');
  }
}

document.querySelectorAll('[id^="ProductUpload-"]').forEach((el) => new ProductUpload(el));